/**
 * Results table component
 * Displays places/results in a table format
 */

import { useState } from 'react';
import { Table, Typography, Rate, Tag, Space, Button, Tooltip, Avatar } from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import {
  EnvironmentOutlined,
  PhoneOutlined,
  GlobalOutlined,
  EyeOutlined,
  ExportOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import type { PlaceListItem } from '@/types/place';
import { formatNumber } from '@/utils/formatters';

const { Text, Link } = Typography;

interface ResultsTableProps {
  places: PlaceListItem[];
  loading?: boolean;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, pageSize: number) => void;
  };
  selectedRowKeys?: string[];
  onSelectionChange?: (keys: string[]) => void;
  onPlaceClick?: (place: PlaceListItem) => void;
  onRescan?: (place: PlaceListItem) => void;
  rescanningId?: string | number | null;
  visibleColumns?: string[];
}

export function ResultsTable({
  places,
  loading,
  pagination,
  selectedRowKeys = [],
  onSelectionChange,
  onPlaceClick,
  onRescan,
  rescanningId,
  visibleColumns,
}: ResultsTableProps) {
  const allColumns: ColumnsType<PlaceListItem> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 220,
      ellipsis: true,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name: string, record) => (
        <Space>
          {record.thumbnail && (
            <Avatar src={record.thumbnail} size="small" shape="square" />
          )}
          <Link
            href={record.googleMapsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name)}&query_place_id=${record.placeId}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontWeight: 500 }}
            onClick={(e) => e.stopPropagation()}
            data-google-place-id={record.placeId}
          >
            {name}
          </Link>
        </Space>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      ellipsis: true,
      render: (category: string) =>
        category ? <Tag>{category}</Tag> : <Text type="secondary">-</Text>,
    },
    {
      title: 'Rating',
      dataIndex: 'rating',
      key: 'rating',
      width: 180,
      sorter: (a, b) => (a.rating || 0) - (b.rating || 0),
      render: (rating: number, record) =>
        rating ? (
          <Space size={4}>
            <Rate disabled value={rating} allowHalf style={{ fontSize: 12 }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              ({formatNumber(record.reviewCount || 0)})
            </Text>
          </Space>
        ) : (
          <Text type="secondary">No rating</Text>
        ),
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      width: 250,
      ellipsis: true,
      render: (address: string) =>
        address ? (
          <Tooltip title={address}>
            <Space>
              <EnvironmentOutlined style={{ color: 'var(--color-text-tertiary)' }} />
              <Text style={{ fontSize: 13 }}>{address}</Text>
            </Space>
          </Tooltip>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      width: 140,
      render: (phone: string) =>
        phone ? (
          <Space>
            <PhoneOutlined style={{ color: 'var(--color-text-tertiary)' }} />
            <Text copyable style={{ fontSize: 13 }}>
              {phone}
            </Text>
          </Space>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: 'Website',
      dataIndex: 'website',
      key: 'website',
      width: 100,
      render: (website: string) =>
        website ? (
          <Button
            type="link"
            size="small"
            icon={<GlobalOutlined />}
            href={website}
            target="_blank"
            style={{ padding: 0 }}
          >
            Visit
          </Button>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: 'Coordinates',
      key: 'coordinates',
      width: 180,
      render: (_, record) =>
        record.latitude && record.longitude ? (
          <Text style={{ fontSize: 12 }} copyable>
            {record.latitude.toFixed(6)}, {record.longitude.toFixed(6)}
          </Text>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: 'Price',
      dataIndex: 'priceLevel',
      key: 'priceLevel',
      width: 80,
      align: 'center',
      render: (level: string) =>
        level ? (
          <Text>{level}</Text>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: 'Reviews',
      dataIndex: 'reviewCount',
      key: 'reviewCount',
      width: 100,
      sorter: (a, b) => (a.reviewCount || 0) - (b.reviewCount || 0),
      render: (count: number) =>
        count !== null && count !== undefined ? (
          <Text>{formatNumber(count)}</Text>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: 'Plus Code',
      dataIndex: 'plusCode',
      key: 'plusCode',
      width: 140,
      ellipsis: true,
      render: (plusCode: string) =>
        plusCode ? (
          <Text copyable style={{ fontSize: 12 }}>
            {plusCode}
          </Text>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: 'Hours',
      dataIndex: 'openingHours',
      key: 'openingHours',
      width: 140,
      ellipsis: true,
      render: (hours: string | object) => {
        if (!hours) return <Text type="secondary">-</Text>;
        // Handle both string and object formats
        const hoursText = typeof hours === 'string' ? hours : JSON.stringify(hours);
        return (
          <Tooltip title={hoursText}>
            <Space>
              <ClockCircleOutlined style={{ color: 'var(--color-text-tertiary)' }} />
              <Text style={{ fontSize: 12 }} ellipsis>
                {hoursText.substring(0, 30)}...
              </Text>
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'Verified',
      dataIndex: 'ownerVerified',
      key: 'ownerVerified',
      width: 80,
      align: 'center',
      render: (verified: boolean) =>
        verified ? (
          <CheckCircleOutlined style={{ color: 'var(--ant-color-success)', fontSize: 16 }} />
        ) : (
          <CloseCircleOutlined style={{ color: 'var(--ant-color-text-tertiary)', fontSize: 16 }} />
        ),
    },
    {
      title: 'Job ID',
      dataIndex: 'jobId',
      key: 'jobId',
      width: 120,
      ellipsis: true,
      render: (jobId: string) =>
        jobId ? (
          <Text copyable style={{ fontSize: 11 }}>
            {jobId.substring(0, 8)}...
          </Text>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: 'Scraped',
      dataIndex: 'scrapedAt',
      key: 'scrapedAt',
      width: 150,
      sorter: (a, b) => {
        const dateA = a.scrapedAt ? new Date(a.scrapedAt).getTime() : 0;
        const dateB = b.scrapedAt ? new Date(b.scrapedAt).getTime() : 0;
        return dateA - dateB;
      },
      render: (date: string) =>
        date ? (
          <Tooltip title={new Date(date).toLocaleString()}>
            <Text style={{ fontSize: 12 }}>
              {new Date(date).toLocaleDateString()}
            </Text>
          </Tooltip>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => {
        const isRescanning = rescanningId === record.id || rescanningId === record.placeId;
        return (
          <Space size={4}>
            <Tooltip title="View details">
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  onPlaceClick?.(record);
                }}
              />
            </Tooltip>
            {onRescan && (
              <Tooltip title="Rescan place to refresh data">
                <Button
                  type="text"
                  size="small"
                  icon={<SyncOutlined spin={isRescanning} />}
                  loading={isRescanning}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRescan(record);
                  }}
                  disabled={isRescanning}
                />
              </Tooltip>
            )}
            {record.googleMapsUrl && (
              <Tooltip title="Open in Google Maps">
                <Button
                  type="text"
                  size="small"
                  icon={<ExportOutlined />}
                  href={record.googleMapsUrl}
                  target="_blank"
                  onClick={(e) => e.stopPropagation()}
                  data-google-place-id={record.placeId}
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  // Filter columns based on visibility
  const columns = visibleColumns
    ? allColumns.filter(
        (col) =>
          col.key === 'actions' || // Always show actions
          col.key === 'name' || // Always show name
          visibleColumns.includes(col.key as string)
      )
    : allColumns;

  return (
    <Table
      columns={columns}
      dataSource={places}
      rowKey="placeId"
      loading={loading}
      pagination={
        pagination
          ? {
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              onChange: pagination.onChange,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${formatNumber(total)} places`,
            }
          : false
      }
      rowSelection={
        onSelectionChange
          ? {
              selectedRowKeys,
              onChange: (keys) => onSelectionChange(keys as string[]),
              preserveSelectedRowKeys: true, // Keep selections across page changes
            }
          : undefined
      }
      scroll={{ x: 1200 }}
      size="small"
      locale={{ emptyText: 'No places found' }}
      onRow={(record) => ({
        onClick: () => onPlaceClick?.(record),
        style: { cursor: 'pointer' },
        'data-google-place-id': record.placeId,
      })}
    />
  );
}

export default ResultsTable;
